
import { z } from 'zod';
import { SESSION_TYPES, BELT_RANKS, STRIPE_COUNTS } from '../../constants';
import type { TrainingFormValues, SessionType } from '@/types/training';
import { loadPersistentFields } from '../../utils/storageUtils';

export const trainingFormSchema = z.object({
  sessionType: z.enum(SESSION_TYPES).optional(),
  date: z.date(),
  duration: z.union([z.number(), z.string()]).transform((val: unknown) => {
    if (val === null || val === undefined || val === '') return 60;
    const num = typeof val === 'string' ? parseInt(val, 10) : val as number;
    return isNaN(num) ? 60 : Math.max(1, Math.min(600, num));
  }),
  professor: z.string().default(''),
  techniques: z.union([z.string(), z.array(z.string())]).default(''),
  location: z.string().default(''),
  notes: z.string().default(''),
  learned: z.string().default(''),
  improvements: z.string().default(''),
  successes: z.string().default(''),
  youtube_url: z.string().default(''),
  belt_rank: z.enum(BELT_RANKS).optional(),
  stripe_count: z.enum(STRIPE_COUNTS).optional(),
  // Competition fields
  competition_total_matches: z.number().min(0).default(0),
  competition_weight_classes: z.string().default(''),
  competition_total_wins: z.number().min(0).default(0),
  competition_finish: z.string().default(''),
  competition_matches: z.array(z.object({
    id: z.string().optional(),
    gi_nogi: z.enum(['gi', 'nogi']),
    weight_class: z.string(),
    result: z.enum(['win', 'loss']),
    method: z.enum(['points', 'submission', 'dq']),
    video_url: z.string().default(''),
    division: z.enum(['Child', 'Juvenile 1', 'Juvenile 2', 'Adult', 'Absolute', 'Masters 1', 'Masters 2', 'Masters 3', 'Masters 4', 'Masters 5', 'Masters 6', 'Other'])
  })).default([]),
});

export type TrainingFormSchemaType = z.infer<typeof trainingFormSchema>;

export const getDefaultValues = (isEditing: boolean = false): Partial<TrainingFormValues> => {
  // For editing, don't load persistent fields - use actual session data
  if (isEditing) {
    return {
      sessionType: 'Gi',
      date: new Date(),
      duration: 60,
      professor: '',
      techniques: '',
      location: '',
      learned: '',
      improvements: '',
      successes: '',
      notes: '',
      youtube_url: '',
      belt_rank: 'white',
      stripe_count: 'no stripes',
      competition_total_matches: 0,
      competition_weight_classes: '',
      competition_total_wins: 0,
      competition_finish: '',
      competition_matches: []
    };
  }
  
  // For new sessions, load persistent fields and fresh defaults for other fields
  const persistentFields = loadPersistentFields();
  const homeGym = typeof window !== 'undefined' ? localStorage.getItem('user-home-gym') || '' : '';
  
  return {
    sessionType: (persistentFields.sessionType as SessionType) || 'Gi',
    date: new Date(),
    duration: persistentFields.duration || 60,
    professor: persistentFields.professor || '',
    techniques: '', // Always start fresh
    location: persistentFields.location || homeGym,
    learned: '', // Always start fresh
    improvements: '', // Always start fresh
    successes: '', // Always start fresh
    notes: '', // Always start fresh
    youtube_url: '', // Always start fresh
    belt_rank: 'white',
    stripe_count: 'no stripes',
    competition_total_matches: 0,
    competition_weight_classes: '',
    competition_total_wins: 0,
    competition_finish: '',
    competition_matches: []
  };
};
